"use strict";
/* globals module */
/**
 * @module userProfileClient
 * @description  User Profile Client provides an easy way to consume  User Profile REST API end-points. In order to obtain needed routes `userProfileClient` uses `userProfileRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var _1 = require("./");
var UserProfileClient = /** @class */ (function () {
    function UserProfileClient(userProfileACLClient, userProfileRoute, apiClient, userEducationClient, userProfileAvatarClient, userSkillClient, userWorkClient) {
        this.userProfileACLClient = userProfileACLClient;
        this.userProfileRoute = userProfileRoute;
        this.apiClient = apiClient;
        this.userEducationClient = userEducationClient;
        this.userProfileAvatarClient = userProfileAvatarClient;
        this.userSkillClient = userSkillClient;
        this.userWorkClient = userWorkClient;
    }
    Object.defineProperty(UserProfileClient.prototype, "acl", {
        get: function () {
            return this.userProfileACLClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserProfileClient.prototype, "routeDefinition", {
        get: function () {
            return this.userProfileRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserProfileClient.prototype, "education", {
        get: function () {
            return this.userEducationClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserProfileClient.prototype, "avatar", {
        get: function () {
            return this.userProfileAvatarClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserProfileClient.prototype, "skill", {
        get: function () {
            return this.userSkillClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UserProfileClient.prototype, "work", {
        get: function () {
            return this.userWorkClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of user profile resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example userProfileClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    UserProfileClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the user profile resource.
     * @method
     * @param id User profile id which uniquely identifies user profile resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example userProfileClient.get()
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    UserProfileClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.userProfileRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create user profile action has been performed; this action creates a new user profile resource.
     * @method
     * @param data An user profile object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create user profile action has been performed.
     * @example userProfileClient.create({
                    firstName : '<first-name>',
                    lastName : '<last-name>',
                    displayName: '<nick-name>'
                }.then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    UserProfileClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update user profile action has been performed; this action updates a user profile resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userProfileRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(userProfile);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An user profile object used to update specified user profile resource.
     * @returns A promise that is resolved once the update user profile action has been performed.
     * @example // userProfile is a resource previously fetched using get action.
                       userProfile.displayName = '<nick-name>';
                       userProfileClient.update(userProfile)
                           .then(function (data) {
                               // perform success action here
                           },
                            function (response, status, headers, config) {
                                // perform error handling here
                           });
    **/
    UserProfileClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a user profile resource from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `userProfileRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(userProfile);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An user profile object used to delete specified user profile resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example // userProfile is a resource previously fetched using get action.
                    userProfileClient.remove(userProfile)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    UserProfileClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    var _a, _b, _c, _d, _e, _f, _g;
    UserProfileClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.UserProfileACLClient)),
        tslib_1.__param(1, inversify_1.inject(_1.TYPES.UserProfileRoute)),
        tslib_1.__param(2, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.UserEducationClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.UserProfileAvatarClient)),
        tslib_1.__param(5, inversify_1.inject(_1.TYPES.UserSkillClient)),
        tslib_1.__param(6, inversify_1.inject(_1.TYPES.UserWorkClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.UserProfileACLClient !== "undefined" && _1.UserProfileACLClient) === "function" ? _a : Object, typeof (_b = typeof _1.UserProfileRoute !== "undefined" && _1.UserProfileRoute) === "function" ? _b : Object, typeof (_c = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _c : Object, typeof (_d = typeof _1.UserEducationClient !== "undefined" && _1.UserEducationClient) === "function" ? _d : Object, typeof (_e = typeof _1.UserProfileAvatarClient !== "undefined" && _1.UserProfileAvatarClient) === "function" ? _e : Object, typeof (_f = typeof _1.UserSkillClient !== "undefined" && _1.UserSkillClient) === "function" ? _f : Object, typeof (_g = typeof _1.UserWorkClient !== "undefined" && _1.UserWorkClient) === "function" ? _g : Object])
    ], UserProfileClient);
    return UserProfileClient;
}());
exports.UserProfileClient = UserProfileClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route definition.
 */ 
